import React, { useEffect, useLayoutEffect, useRef } from "react";
// @ts-ignore
import { Previewer, registerHandlers } from "pagedjs";
import { T } from "services/lang/LanguageService";
import { isProd, queryParam } from "utils/utils";
import { useGetDashboard } from "api/api-hooks/dashboards-hooks";
import getReportPagedJsStyle from "./report-style";
import DashboardReport from "./DashboardReportWrapper";
import PagedJsReportHandler from "./PagedJsReportHandler";

const PROD_GENERATION_TIMEOUT = 40000;
const DEV_GENERATION_TIMEOUT = 5000;
const GEN_DELAY = 1000;

const REPORT_EVT = "dashboard-report-status";

declare global {
  interface Window {
    loadedPreview: boolean;
    reportGenStart: Date;
  }

  interface Element {
    style: any;
  }
}

const DashboardReportViewPaged = () => {
  const dashboardId = parseInt(queryParam("dashboardId")!!);
  const referenceTime = queryParam("referenceTime");
  const isBot = queryParam("robot") === "true";

  const query = useGetDashboard(dashboardId);
  const reportOptions = query.data?.reportOptions;

  const loadingTimoutRef = useRef<NodeJS.Timeout | null>(null);

  const isValid = () => {
    if (!referenceTime) return false;

    // Check if date valid
    const date = new Date(referenceTime);
    return !isNaN(date.getTime());
  };

  const dispatchReportStatus = (status: "finished" | "error") => {
    window.dispatchEvent(new CustomEvent(REPORT_EVT, { detail: status }));
  };

  const userAlert = (message: string) => {
    if (!isBot) alert(message);
  };

  const reportComponentsStatusCallback = (componentsStatus: "finished" | "error") => {
    console.log("Dashboard components loading status", componentsStatus);

    if (loadingTimoutRef.current) clearTimeout(loadingTimoutRef.current);

    if (componentsStatus === "error") {
      dispatchReportStatus("error");
      userAlert(T("alert.error-generating-report-some-component-failed"));
    }

    if (componentsStatus === "finished") {
      setTimeout(() => {
        renderPagePreview();
      }, GEN_DELAY);
    }
  };

  const renderPagePreview = () => {
    window.loadedPreview = true;
    window.reportGenStart = new Date();

    console.log(`Render start time: ${window.reportGenStart.toISOString()}`);

    const rootEl = document.querySelector("#root")!!;
    const pagedPreviewEl = document.querySelector("#preview-paged")!!;

    // Inline style for paged-js margin sizes
    registerHandlers(PagedJsReportHandler);

    const previewer = new Previewer();
    previewer
      .preview(
        rootEl.innerHTML,
        [`${window.jsBasePath}/assets/reports/core.css`, getReportPagedJsStyle(reportOptions!!)],
        pagedPreviewEl
      )
      .then((flow: any) => {
        document.querySelector("#dashboard-report-wrapper-paged")!!.style.display = "none";

        const reportGenEnd = new Date();
        const elapsedSeconds = Math.round(
          (reportGenEnd.getTime() - window.reportGenStart.getTime()) / 1000
        );

        console.log(`Render end time ${reportGenEnd.toISOString()}`);
        console.log(`Rendered total ${flow.total} pages. Took ${elapsedSeconds} seconds.`);

        dispatchReportStatus("finished");
        userAlert(T("alert.finished-generating-report-preview"));
      });
  };

  useEffect(() => {
    console.log("Robot?", isBot);
  }, []);

  const getTimeoutValue = () => {
    if (reportOptions?.meta?.generationTimeout) {
      return reportOptions.meta.generationTimeout * 1000;      
    } 
    return isProd() ? PROD_GENERATION_TIMEOUT : DEV_GENERATION_TIMEOUT;
  }

  useLayoutEffect(() => {
    if (!isValid()) return;
    if (!query.data) return;
    if (window.loadedPreview) return;
    loadingTimoutRef.current = setTimeout(
      () => {
        dispatchReportStatus("error");
        userAlert(
          T(
            "alert.report-generation-reached-timeout-some-components-taking-longer-than-expected-to-load"
          )
        );
      },
      getTimeoutValue()
    );

    if (loadingTimoutRef.current) clearTimeout(loadingTimoutRef.current);
  });

  return (
    <>
      {!isValid() && (
        <div>{T("error.invalid-report-options-missing-or-invalid-referencetime")}</div>
      )}

      {isValid() && !query.data && T("word.loading")}

      {isValid() && query.data && (
        <DashboardReport
          dashboard={query.data}
          referenceTime={referenceTime!!}
          finishCallback={reportComponentsStatusCallback}
        />
      )}
    </>
  );
};

export default DashboardReportViewPaged;
